import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import ScrollToTop from "./components/ScrollToTop";
import GlobalHeader from "./components/GlobalHeader";
import StickyBookingButton from "./components/StickyBookingButton";
import ErrorBoundary from "./components/ErrorBoundary";
import { loadFareHarborScript } from "./utils/fareHarborUtils";

import Index from "./pages/Index";
import About from "./pages/About";
import Tours from "./pages/Tours";
import Gallery from "./pages/Gallery";
import Blog from "./pages/Blog";
import Contact from "./pages/Contact";
import NotFound from "./pages/NotFound";
import FinancialDistrict from "./pages/tours/FinancialDistrict";
import BrooklynHeights from "./pages/tours/BrooklynHeights";
import LowerEastSide from "./pages/tours/LowerEastSide";
import PrivateTours from "./pages/tours/PrivateTours";
import PressKit from "./pages/PressKit";
import Booking from "./pages/Booking";
import InTheNews from "./pages/InTheNews";
import Sitemap from "./pages/Sitemap";
import Survey from "./pages/Survey";
import PetitionJerryFox from "./pages/PetitionJerryFox";

// Create a new QueryClient for client-side rendering
// This will be different from the one used during SSR
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Preload FareHarbor script when idle
const preloadFareHarborScript = () => {
  if (typeof window !== "undefined") {
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(() => {
        loadFareHarborScript().catch(e => console.error("Failed to preload FareHarbor script:", e));
      });
    } else {
      setTimeout(() => {
        loadFareHarborScript().catch(e => console.error("Failed to preload FareHarbor script:", e));
      }, 3000);
    }
  }
};

// Only execute in browser environment
if (typeof window !== "undefined") {
  preloadFareHarborScript();
}

// Custom component to handle dynamic redirects with location
const BlogRedirect = () => {
  const location = useLocation();
  const postId = location.pathname.split('/')[2];
  return <Navigate to={`/blog?post=${postId}`} replace />;
};

const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <HelmetProvider>
          <ErrorBoundary>
            <TooltipProvider>
              <ScrollToTop />
              <GlobalHeader />
              <Navbar />
              <div className="flex flex-col min-h-screen">
                <main className="flex-grow" id="main-content">
                  <Routes>
                    <Route path="/" element={<Index />} />
                    <Route path="/about" element={<About />} />
                    <Route path="/tours" element={<Tours />} />
                    <Route path="/tours/financial-district" element={<FinancialDistrict />} />
                    <Route path="/tours/brooklyn-heights" element={<BrooklynHeights />} />
                    <Route path="/tours/lower-east-side" element={<LowerEastSide />} />
                    <Route path="/tours/private-tours" element={<PrivateTours />} />
                    <Route path="/gallery" element={<Gallery />} />
                    <Route path="/blog" element={<Blog />} />
                    <Route path="/contact" element={<Contact />} />
                    <Route path="/petition-jerry-fox" element={<PetitionJerryFox />} />
                    <Route path="/press-kit" element={<PressKit />} />
                    <Route path="/booking" element={<Booking />} />
                    <Route path="/in-the-news" element={<InTheNews />} />
                    <Route path="/sitemap" element={<Sitemap />} />
                    <Route path="/survey" element={<Survey />} />
                    <Route path="/sitemap.html" element={<Navigate to="/sitemap" replace />} />
                    
                    {/* 404 URL pattern redirects for broken and outdated URLs */}
                    
                    {/* Redirects for old URL format */}
                    <Route path="/financial-district-cat-tour" element={<Navigate to="/tours/financial-district" replace />} />
                    <Route path="/brooklyn-heights-cat-tour" element={<Navigate to="/tours/brooklyn-heights" replace />} />
                    <Route path="/lower-east-side-cat-tour" element={<Navigate to="/tours/lower-east-side" replace />} />
                    <Route path="/private-cat-tours" element={<Navigate to="/tours/private-tours" replace />} />
                    
                    {/* Additional common variations */}
                    <Route path="/financial-district" element={<Navigate to="/tours/financial-district" replace />} />
                    <Route path="/brooklyn-heights" element={<Navigate to="/tours/brooklyn-heights" replace />} />
                    <Route path="/lower-east-side" element={<Navigate to="/tours/lower-east-side" replace />} />
                    <Route path="/private-tours" element={<Navigate to="/tours/private-tours" replace />} />
                    
                    {/* Redirects for commonly misspelled or alternative URLs */}
                    <Route path="/tour" element={<Navigate to="/tours" replace />} />
                    <Route path="/cat-tours" element={<Navigate to="/tours" replace />} />
                    <Route path="/tourinfo" element={<Navigate to="/tours" replace />} />
                    <Route path="/cat-tour" element={<Navigate to="/tours" replace />} />
                    <Route path="/cats" element={<Navigate to="/" replace />} />
                    <Route path="/brooklyn" element={<Navigate to="/tours/brooklyn-heights" replace />} />
                    <Route path="/about-us" element={<Navigate to="/about" replace />} />
                    <Route path="/about-peggy" element={<Navigate to="/about" replace />} />
                    <Route path="/contactus" element={<Navigate to="/contact" replace />} />
                    <Route path="/contact-us" element={<Navigate to="/contact" replace />} />
                    <Route path="/book" element={<Navigate to="/booking" replace />} />
                    <Route path="/book-a-tour" element={<Navigate to="/booking" replace />} />
                    <Route path="/book-tour" element={<Navigate to="/booking" replace />} />
                    <Route path="/reserve" element={<Navigate to="/booking" replace />} />
                    <Route path="/reservation" element={<Navigate to="/booking" replace />} />
                    <Route path="/press" element={<Navigate to="/press-kit" replace />} />
                    <Route path="/media" element={<Navigate to="/press-kit" replace />} />
                    <Route path="/news" element={<Navigate to="/in-the-news" replace />} />
                    <Route path="/media-coverage" element={<Navigate to="/in-the-news" replace />} />
                    <Route path="/photo" element={<Navigate to="/gallery" replace />} />
                    <Route path="/photos" element={<Navigate to="/gallery" replace />} />
                    <Route path="/image" element={<Navigate to="/gallery" replace />} />
                    <Route path="/images" element={<Navigate to="/gallery" replace />} />
                    <Route path="/articles" element={<Navigate to="/blog" replace />} />
                    <Route path="/stories" element={<Navigate to="/blog" replace />} />
                    <Route path="/post" element={<Navigate to="/blog" replace />} />
                    <Route path="/posts" element={<Navigate to="/blog" replace />} />
                    
                    {/* Legacy numeric IDs for blog posts - using custom component for dynamic redirect */}
                    <Route path="/blog/:id" element={<BlogRedirect />} />
                    
                    {/* Handle old site structure URLs */}
                    <Route path="/index.html" element={<Navigate to="/" replace />} />
                    <Route path="/index.php" element={<Navigate to="/" replace />} />
                    <Route path="/home" element={<Navigate to="/" replace />} />
                    <Route path="/about.html" element={<Navigate to="/about" replace />} />
                    <Route path="/contact.html" element={<Navigate to="/contact" replace />} />
                    <Route path="/gallery.html" element={<Navigate to="/gallery" replace />} />
                    <Route path="/blog.html" element={<Navigate to="/blog" replace />} />
                    
                    {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </main>
                <Footer />
              </div>
              <StickyBookingButton link="/booking" />
              <Toaster />
              <Sonner />
            </TooltipProvider>
          </ErrorBoundary>
        </HelmetProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
};

export default App;
